/**
 * Turning a table of cells into recipient pairs.
 *
 * Shared by the CSV reader and the XLSX reader, because "which column holds the
 * phone numbers?" and "is the first row a header?" are the same two questions
 * whichever file the rows came out of — and two answers that drifted apart
 * would mean the same spreadsheet imported differently depending on which
 * format it was saved in.
 *
 * Pure by construction (PLAN §4).
 */

import { isValidPhone } from './phone.ts';
import type { ParsedPair } from './recipients.ts';

export interface TableRow {
  /** 1-based row in the source, for error messages. */
  readonly line: number;
  readonly fields: readonly string[];
}

export type TableProblem = 'empty' | 'single-column';

export interface TableOutcome {
  readonly pairs: readonly ParsedPair[];
  readonly hasHeader: boolean;
  readonly phoneColumn: number;
  readonly codeColumn: number;
  /** Data rows, excluding any header. */
  readonly rows: number;
  readonly problem?: TableProblem;
}

/** Header words worth recognising, in both languages the panel speaks. */
const PHONE_HEADERS = ['телефон', 'номер', 'phone', 'msisdn', 'mobile', 'tel', 'number'];
const CODE_HEADERS = ['промокод', 'промо', 'код', 'promo', 'code', 'coupon'];

/**
 * Reads rows into pairs, working out for itself whether the first row is a
 * header and which columns hold the number and the code.
 */
export function pairsFromRows(rows: readonly TableRow[]): TableOutcome {
  const populated = rows.filter((row) => row.fields.some((value) => value !== ''));

  if (populated.length === 0) return refuse('empty');
  if ((populated[0] as TableRow).fields.length < 2) {
    // One column cannot carry a pair — usually the wrong delimiter, sometimes
    // the wrong file. Either way there is nothing to import.
    return refuse('single-column');
  }

  const hasHeader = looksLikeHeader(populated);
  const body = hasHeader ? populated.slice(1) : populated;
  const columns = chooseColumns(hasHeader ? (populated[0] as TableRow) : undefined, body);

  const pairs: ParsedPair[] = body.map((row) => ({
    line: row.line,
    phone: row.fields[columns.phone] ?? '',
    promoCode: row.fields[columns.code] ?? '',
  }));

  return {
    pairs,
    hasHeader,
    phoneColumn: columns.phone,
    codeColumn: columns.code,
    rows: pairs.length,
  };
}

/**
 * Whether the first row is a header.
 *
 * Deliberately reluctant. Calling a header a header costs nothing; calling a
 * *data* row a header drops a customer from the campaign silently, which is the
 * one outcome this program never allows. So a row is a header only when it
 * holds no phone number **and** either names itself as one — a recognised
 * header word — or contains no digits anywhere, which no phone number and no
 * promo code this program has seen manages.
 *
 * A row of unrecognised words with a digit in it therefore imports as one
 * invalid row: visible, explained, and fixable, rather than gone.
 */
export function looksLikeHeader(rows: readonly TableRow[]): boolean {
  const first = rows[0];
  if (first === undefined) return false;
  if (first.fields.some((value) => isValidPhone(value))) return false;

  const names = first.fields.map((value) => value.toLowerCase());
  const named = names.some((name) =>
    [...PHONE_HEADERS, ...CODE_HEADERS].some((word) => name.includes(word)),
  );
  // Catches a header in a language nobody listed: `Nummer;Gutschein`.
  const wordsOnly = !first.fields.some((value) => /\d/.test(value));

  return named || wordsOnly;
}

function chooseColumns(
  header: TableRow | undefined,
  body: readonly TableRow[],
): { readonly phone: number; readonly code: number } {
  const byName = header === undefined ? undefined : columnsFromHeader(header);
  if (byName !== undefined) return byName;

  // No usable header: the column that is mostly phone numbers is the phone
  // column, whatever position it happens to be in.
  const width = Math.max(...body.map((row) => row.fields.length), 2);
  let phone = 0;
  let bestShare = 0;
  for (let column = 0; column < width; column += 1) {
    const share = phoneShare(body, column);
    if (share > bestShare) {
      bestShare = share;
      phone = column;
    }
  }

  // The code is the nearest column to its right that holds anything, so an
  // export with a trailing empty column does not silently import blanks.
  for (let column = phone + 1; column < width; column += 1) {
    if (body.some((row) => (row.fields[column] ?? '') !== '')) return { phone, code: column };
  }
  return { phone, code: phone === 0 ? 1 : 0 };
}

function columnsFromHeader(header: TableRow): { phone: number; code: number } | undefined {
  const names = header.fields.map((value) => value.toLowerCase());
  const phone = names.findIndex((name) => PHONE_HEADERS.some((word) => name.includes(word)));
  const code = names.findIndex((name) => CODE_HEADERS.some((word) => name.includes(word)));

  if (phone === -1 || code === -1 || phone === code) return undefined;
  return { phone, code };
}

function phoneShare(rows: readonly TableRow[], column: number): number {
  if (rows.length === 0) return 0;
  const valid = rows.filter((row) => isValidPhone(row.fields[column] ?? '')).length;
  return valid / rows.length;
}

function refuse(problem: TableProblem): TableOutcome {
  return { pairs: [], hasHeader: false, phoneColumn: 0, codeColumn: 1, rows: 0, problem };
}
